import { getSources, SOURCES, type Source } from "@/lib/sources";
import { track } from "@/lib/analytics";

/**
 * A numbered reference list, set like the notes at the back of a report: number,
 * title, publisher, one line on what it supports. Before V5 each source was its own
 * card with a coloured publisher pill.
 */
function SourceItem({ source, n }: { source: Source; n: number }) {
  return (
    <li className="grid grid-cols-[1.75rem_1fr] gap-x-2 py-3.5">
      <span className="tabular pt-px text-sm text-muted-foreground">{n}.</span>
      <div className="min-w-0">
        <a
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("source_clicked", { id: source.id, org: source.org })}
          className="font-medium text-foreground underline decoration-border underline-offset-4 transition-colors hover:text-primary hover:decoration-current"
        >
          {source.title}
          <span aria-hidden className="ml-1 text-muted-foreground">
            ↗
          </span>
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
        <span className="text-sm text-muted-foreground"> · {source.org}</span>
        {source.note && (
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{source.note}</p>
        )}
      </div>
    </li>
  );
}

export function SourceList({
  ids,
  sources,
  title = "Sources",
  collapsible = false,
  footnote,
}: {
  ids?: string[];
  sources?: Source[];
  title?: string | null;
  /** Show the count and let the reader open the list, rather than spending a screen on it. */
  collapsible?: boolean;
  footnote?: string;
}) {
  const list = sources ?? getSources(ids ?? []);
  if (!list.length) return null;

  const items = (
    <ol className="divide-y divide-border">
      {list.map((s, i) => (
        <SourceItem key={s.id} source={s} n={i + 1} />
      ))}
    </ol>
  );

  if (collapsible) {
    return (
      <details className="group border-y border-border">
        <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 py-3">
          <span>
            <span className="block font-display text-lg text-foreground">{title ?? "Sources"}</span>
            <span className="block text-sm text-muted-foreground">
              {list.length} sources{footnote ? ` · ${footnote}` : ""}
            </span>
          </span>
          <span className="no-print shrink-0 text-sm font-semibold text-primary">
            <span className="group-open:hidden">Show</span>
            <span className="hidden group-open:inline">Hide</span>
          </span>
        </summary>
        <div className="border-t border-border">{items}</div>
      </details>
    );
  }

  return (
    <section className="border-t border-rule/70 pt-4">
      {title && <h2 className="label-caps text-muted-foreground">{title}</h2>}
      <div className={title ? "mt-1" : ""}>{items}</div>
      {footnote && <p className="mt-2 text-xs text-muted-foreground">{footnote}</p>}
    </section>
  );
}

export { SOURCES };
